import supabase from "../supabase-client";

// Shared parser for SAT CFDI 4.0 invoices. Returned shape is consumed by both
// the service-invoice flow (NewService.js) and the car-purchase flow (EditCar.js).

export function parseCfdiInvoice(xmlString) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlString, "text/xml");

  // CFDIs use the "cfdi:" prefix by convention but any prefix bound to the
  // SAT namespace is valid. Look up by local name only so prefix changes
  // don't silently break parsing.
  const byLocal = (root, local) =>
    Array.from(root.getElementsByTagNameNS("*", local));
  const firstByLocal = (root, local) => byLocal(root, local)[0];
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const attr = (node, name) => (node ? node.getAttribute(name) || "" : "");

  const emisor = firstByLocal(xml, "Emisor");
  const emisorNombre = attr(emisor, "Nombre");
  const emisorRfc = attr(emisor, "Rfc");

  const timbre = firstByLocal(xml, "TimbreFiscalDigital");
  let uuid = attr(timbre, "UUID");
  const fechaTimbrado = attr(timbre, "FechaTimbrado");

  const comprobante = firstByLocal(xml, "Comprobante");
  const subTotal = num(attr(comprobante, "SubTotal"));
  const total = num(attr(comprobante, "Total"));

  const serie = attr(comprobante, "Serie");
  const folioNum = attr(comprobante, "Folio");
  let folio = serie + folioNum;
  if (folio === "") folio = uuid;
  if (uuid === "" || uuid === "UUID") uuid = folio;

  // There can be multiple <Impuestos> (one nested in each Concepto plus the
  // comprobante-level summary). Pick the one carrying the total attribute.
  const impuestosNodes = byLocal(xml, "Impuestos");
  const impuestos =
    impuestosNodes.find((n) => n.hasAttribute("TotalImpuestosTrasladados")) ||
    impuestosNodes[0];
  const iva = num(attr(impuestos, "TotalImpuestosTrasladados"));

  const conceptos = byLocal(xml, "Concepto").map((c) => {
    const descripcion = attr(c, "Descripcion");
    const cantidad = num(attr(c, "Cantidad"));
    const valorUnitario = num(attr(c, "ValorUnitario"));
    const importe = num(attr(c, "Importe"));
    const ivaConcepto = byLocal(c, "Traslado").reduce(
      (sum, t) => sum + num(t.getAttribute("Importe")),
      0
    );
    // Expose attrs useful downstream (e.g. VIN lives in NoIdentificacion)
    const noIdentificacion = attr(c, "NoIdentificacion");
    return {
      descripcion,
      cantidad,
      valorUnitario,
      importe,
      iva: ivaConcepto,
      total: importe + ivaConcepto,
      noIdentificacion,
    };
  });

  // UUIDs this CFDI substitutes (CfdiRelacionados TipoRelacion="04"): the
  // provider cancelled those and issued this one in their place.
  const substitutes = byLocal(xml, "CfdiRelacionados")
    .filter((n) => attr(n, "TipoRelacion") === "04")
    .flatMap((n) => byLocal(n, "CfdiRelacionado").map((r) => attr(r, "UUID")))
    .filter(Boolean);

  // Preserve the parsed XML so callers can dig into non-standard namespaces
  // (e.g., ventavehiculos:VentaVehiculos for car-purchase invoices).
  return {
    emisorNombre,
    emisorRfc,
    uuid,
    folio,
    fechaTimbrado,
    subTotal,
    total,
    iva,
    conceptos,
    substitutes,
    _xml: xml,
  };
}

// --- Vehicle extraction (AI) -------------------------------------------------
//
// The car-specific fields (marca, modelo comercial, color, motor, transmisión)
// live in free text inside Concepto/@Descripcion, written differently by every
// dealership — and often repeated more cleanly inside an <Addenda> that a
// regex over the descripción never sees. So interpretation is delegated to the
// `parse_car_invoice` edge function, which sends the whole XML to Claude and
// also resolves the car_models match.
//
// What stays deterministic here: price, folio, date and emisor, which come from
// fixed CFDI attributes and should never be restated by a model.

export function extractInvoiceFacts(cfdi) {
  // Acquisition price = taxable base (total sin IVA). Rounded to 2 decimals
  // to avoid float noise from the subtraction.
  const acquisition_price =
    Math.round(((cfdi?.total || 0) - (cfdi?.iva || 0)) * 100) / 100;
  return {
    acquisition_price,
    start_date: (cfdi?.fechaTimbrado || "").split("T")[0] || "",
    invoice_id: cfdi?.folio || "",
    emisor_name: cfdi?.emisorNombre || "",
    emisor_rfc: cfdi?.emisorRfc || "",
  };
}

// Ask the edge function to read the invoice. Returns
// { vehicle, matched_model_id, model_match_confidence, model_match_reason,
//   warnings }. Throws on failure — the caller decides what to show.
export async function extractVehicleDataAI(xmlString) {
  const { data, error } = await supabase.functions.invoke(
    "parse_car_invoice",
    { body: { xml: xmlString } }
  );
  if (error) {
    // supabase-js hides the function's own error body inside the response.
    let detail = "";
    try {
      detail = (await error.context?.json())?.error || "";
    } catch {
      /* non-JSON body */
    }
    throw new Error(detail || error.message || "Error al leer la factura.");
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.vehicle) throw new Error("Respuesta vacía del lector de facturas.");
  return data;
}

// A VIN the model returned is only trustworthy if it looks like a VIN: 17
// chars, and the SAT/ISO alphabet excludes I, O and Q.
export function isPlausibleVin(vin) {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(String(vin || "").toUpperCase());
}
